import { useMemo } from 'react';
import {
  _ClassesQuery,
  _RoomsQuery,
  PersonRole,
  use_ClassesQuery,
  use_DivisionsQuery,
  use_GradeGroupsQuery,
  use_GroupsQuery,
  use_PeopleQuery,
  use_RoomsQuery,
  use_SubjectContainersQuery,
  use_SubjectsQuery,
} from '../types/planung-graphql-client-defs';
import { sortClasses } from '../utils/sortHelper';
import { useMemorizedCacheTag } from './useMemorizedCacheTag';
import { useSelectedSchoolYear } from './useSelectedSchoolYear';

export const useLoadBasicData = ({
  pause,
  schoolYearUuid,
}: {
  pause: boolean;
  // Optional: Basisdaten eines anderen Schuljahres laden (z.B. Vorjahr). Ohne Angabe wird das
  // aktuell gewählte Schuljahr verwendet. Betrifft die schuljahresabhängigen Daten
  // (Klassen, Gruppen, Divisions) – Fächer/Räume/etc. sind org-weit und jahresunabhängig.
  schoolYearUuid?: string;
}) => {
  const selectedSchoolYear = useSelectedSchoolYear();

  const effectiveSchoolYearUuid = schoolYearUuid ?? selectedSchoolYear?.uuid;

  const classContext = useMemorizedCacheTag('CLASSES');
  const teacherContext = useMemorizedCacheTag('PERSONS');
  const roomContext = useMemorizedCacheTag('ROOMS');
  const subjectContext = useMemorizedCacheTag('SUBJECTS');
  const subjectContainerContext = useMemorizedCacheTag('SUBJECT_CONTAINER');
  const gradeGroupContext = useMemorizedCacheTag('GRADE_GROUPS');
  const groupContext = useMemorizedCacheTag('GROUPS');
  const divisionContext = useMemorizedCacheTag('DIVISIONS');

  const [{ data: teacherData }, refetchTeacherData] = use_PeopleQuery({
    context: teacherContext,
    pause,
  });

  const filteredPersons = useMemo(() => {
    return {
      people: teacherData?.people.filter((t) => t.active) ?? [],
    };
  }, [teacherData?.people]);

  const filteredTeachers = useMemo(() => {
    return {
      people: teacherData?.people.filter((t) => t.active && t.role === PersonRole.Teacher) ?? [],
    };
  }, [teacherData?.people]);

  const [{ data: _roomsData }] = use_RoomsQuery({
    context: roomContext,
    pause,
  });

  const roomsData: _RoomsQuery = useMemo(() => {
    const sortedRooms = _roomsData?.rooms.sort((a, b) => {
      const numA = parseInt(a.name.match(/^\d+/)?.[0] || 'Infinity', 10);
      const numB = parseInt(b.name.match(/^\d+/)?.[0] || 'Infinity', 10);
      return numA - numB || a.name.localeCompare(b.name);
    });
    return {
      __typename: 'Query',
      rooms: sortedRooms ?? [],
    };
  }, [_roomsData?.rooms]);

  const [{ data: subjectData }] = use_SubjectsQuery({
    context: subjectContext,
    pause,
  });

  const [{ data: subjectContainerData }] = use_SubjectContainersQuery({
    pause,
    context: subjectContainerContext,
  });

  const [{ data: gradeGroupData }] = use_GradeGroupsQuery({
    pause,
    context: gradeGroupContext,
  });

  const [{ data: _classesData }] = use_ClassesQuery({
    variables: { schoolYearUuid: effectiveSchoolYearUuid },
    pause,
    context: classContext,
  });

  const classesData: _ClassesQuery = useMemo(() => {
    const sortedClasses = _classesData?.classes.sort(sortClasses);

    return {
      __typename: 'Query',
      classes: sortedClasses ?? [],
    };
  }, [_classesData?.classes]);

  const [{ data: groupsData }] = use_GroupsQuery({
    variables: { schoolYearUuid: effectiveSchoolYearUuid },
    context: groupContext,
    pause,
  });

  const [{ data: divisionsData }] = use_DivisionsQuery({
    pause,
    variables: { schoolYearUuid: effectiveSchoolYearUuid },
    context: divisionContext,
  });

  return {
    subjectData,
    subjectContainerData,
    roomsData,
    gradeGroupData,
    teacherData: filteredTeachers,
    personData: filteredPersons,
    allPersons: teacherData,
    classesData,
    groupsData,
    divisionsData,
    refetchTeacherData,
  };
};
